import { isPlainObject as isPlainObjectLodash, isUndefined, merge, omitBy } from 'lodash';

/**
 * Optional keys in an object.
 * Similar to `Partial`, but it does not allow `undefined` values.
 */
type Optional<T> = {
    [K in keyof T]?: T[K] extends undefined ? never : T[K];
};

/**
 * Type guard to check if a value is a plain object.
 * @param value Value to check
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
    return isPlainObjectLodash(value);
}

/**
 * Omit undefined values from an object.
 * Does not currently support nested object values.
 * @param object Object to omit undefined values from
 */
export function omitUndefined<T extends Record<string, unknown>>(object: T): Optional<T> {
    return omitBy(object, isUndefined) as Optional<T>;
}

/**
 * Merge objects and omit undefined values. This can be useful when working with `Partial`, as this allows values
 * that are `undefined`, which can potentially overwrite previously defined values.
 * Does not currently support nested object values.
 * @param object The destination object
 * @param source The source object
 */
export function mergeDefined<Object extends Record<string, unknown>, Source extends Record<string, unknown>>(
    object: Object,
    source: Source
): Object & Optional<Source> {
    return merge(object, omitUndefined(source));
}

/**
 * Get all keys from an object, including nested keys.
 * For example, for the object `{ a: { b: 1 }, c: 2 }`, it will return `['a.b', 'c']`.
 * Check the unit tests for more examples.
 * @param object Object with keys
 * @param parentPath Starting path for nested keys. Useful for recursion.
 */
export function flattenKeys(object: Record<string, unknown>, parentPath = ''): string[] {
    const paths: string[] = [];

    for (const [key, value] of Object.entries(object)) {
        const currentPath = parentPath ? `${parentPath}.${key}` : key;

        if (isPlainObject(value)) {
            const nestedPaths = flattenKeys(value, currentPath);
            paths.push(...nestedPaths);
        } else {
            paths.push(currentPath);
        }
    }

    return paths;
}
